<script setup lang="ts">
import { onlineCourseService } from '@/api/services/online-course-service'
import BaseLargeButton from '@/components/base/BaseLargeButton.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseSmallButton from '@/components/base/BaseSmallButton.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import { useModal } from '@/composables/useModal'
import { useToast } from '@/composables/useToast'
import { requestAuthentication } from '@/sdk/bootpay-sdk'
import { useAuthStore } from '@/stores/auth'
import type { OnlineCourse } from '@/types/online-course.types'
import { computed, ref } from 'vue'

const props = defineProps<{
  item: OnlineCourse
}>()

const isMobile = useIsMobile()
const { isOpen, open, close } = useModal()
const authStore = useAuthStore()
const { showToast } = useToast()

const notification = ref(props.item.notification)

// 날짜 포맷 함수
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp)
  const year = date.getFullYear().toString().slice(2)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}.${month}.${day} ${hours}:${minutes} 시작 예정`
}

const isClassInProgress = computed(() => {
  return props.item.startAt < new Date().getTime()
})

const openClass = () => {
  window.open(props.item.zoomUrl, '_blank')
}

const showToggleNotificationModal = async () => {
  if (!authStore.state.account) {
    alert('로그인 후 이용해주세요.')
    return
  }

  if (!authStore.state.account?.authDone) {
    open()
    return
  }

  toggleNotification()
}

const toggleNotification = async () => {
  const message = notification.value ? '알림신청 취소' : '알림신청'

  try {
    const tempNotification = await onlineCourseService.toggleNotification(props.item.idx)

    notification.value = tempNotification
    showToast(message + ' 완료')
  } catch {
    showToast(message + ' 실패', 'error')
  }
}

const showVerifyModal = async () => {
  const isSuccess = await requestAuthentication()

  if (isSuccess) {
    toggleNotification()
  }
}
</script>

<template>
  <div class="online-class-list-item">
    <div class="online-class-list-container">
      <div class="online-class-list-item-thumbnail">
        <img :src="item.thumbnailUrl" alt="온라인 클래스 썸네일" />
      </div>
      <div class="online-class-list-item-content">
        <div class="online-class-list-item-info">
          <div
            class="online-class-list-item-info-status title-sm tint-500"
            :class="{ 'in-progress': isClassInProgress, 'body-lg': isMobile }"
          >
            {{ isClassInProgress ? '수업 진행중' : formatDate(item.startAt) }}
          </div>
          <div class="title-md white" :class="{ 'title-sm': isMobile }">{{ item.title }}</div>
          <div v-if="!isClassInProgress" class="online-class-url-row">
            <img class="svg-24 svg-white" src="@/assets/svgs/link.svg" alt="링크 아이콘" />
            <div class="online-class-url-row-text body-sm white cursor-pointer" @click="openClass">
              온라인 수업 참여링크
            </div>
          </div>
        </div>
        <template v-if="!isMobile">
          <div class="online-class-list-item-buttons">
            <BaseSmallButton
              v-if="isClassInProgress"
              text="수업 참여하기"
              type="main"
              @click="openClass"
            />
            <BaseSmallButton
              v-else
              :text="notification ? '알림신청 취소' : '알림신청'"
              type="sub"
              @click="showToggleNotificationModal"
            />
          </div>
        </template>
        <template v-else>
          <div class="online-class-list-item-buttons">
            <BaseLargeButton
              v-if="isClassInProgress"
              text="수업 참여하기"
              type="main"
              @click="openClass"
            />
            <BaseLargeButton
              v-else
              :text="notification ? '알림신청 취소' : '알림신청'"
              type="sub"
              @click="showToggleNotificationModal"
            />
          </div>
        </template>
      </div>
    </div>

    <BaseModal
      title="알림신청"
      :is-open="isOpen"
      confirm-text="확인"
      cancel-text="취소"
      @confirm="showVerifyModal"
      @close="close"
    >
      <div class="notification-modal-content body-sm gray-200">
        알림톡을 받기 위해 본인인증을 진행해 주세요.
      </div>
    </BaseModal>
  </div>
</template>

<style scoped>
.online-class-list-item {
  width: 100%;
  position: relative;
}

.online-class-list-container {
  display: flex;
  flex-direction: row;
  background-color: transparent;
  border-radius: 8px;
  overflow: hidden;
}

.online-class-list-item-thumbnail {
  position: relative;
  width: 847px;
  height: 477px;
  flex-shrink: 0;
}

.online-class-list-item-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.online-class-list-item-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--gray-800);
  padding: 20px;
  border-radius: 8px;
  margin-left: 20px;
  flex-grow: 1;
}

.online-class-list-item-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.online-class-list-item-buttons {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.online-class-url-row {
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
}

.online-class-url-row-text {
  text-decoration: underline;
}

@media (max-width: 1365px) {
  .online-class-list-container {
    flex-direction: column;
    border-radius: 0px;
  }

  .online-class-list-item-thumbnail {
    width: 100%;
    height: auto;
    aspect-ratio: 375/210;
  }

  .online-class-list-item-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0px;
  }

  .online-class-list-item-content {
    margin-left: 0;
    border-radius: 0px;
    padding: 12px 20px;
    background-color: transparent;
  }

  .online-class-list-item-info {
    gap: 12px;
    margin-bottom: 20px;
  }

  .online-class-list-item-buttons {
    flex-direction: column;
  }
}

.notification-modal-content {
  margin-bottom: 28px;
}
</style>
